import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import classNames from "classnames";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router";
import VLTable from "views/uikit/VLTable";
import { FoldersDropBox } from "../../../../../uikit/FormFields";

import TooltipText from "./TooltipText";
import useCreateDataset from "hooks/useCreateDataset";
import useFolderSelection from "./useFolderSelection";
import useFolderValidation from "hooks/useFolderValidation";
import useFolderData, { FolderTableData } from "./useFolderData";
import { FolderMetadata } from "types";
import { getInvalidFolders } from "redux/CreateDataset/selectors";

import styles from "./style.module.scss";
import Loader from "../../Loader";
import { folderFormats } from "../../../../../uikit/FormFields/FoldersDropBox";
import SupportedFormats from "../../../../../uikit/FormFields/DragAndDropBase/SupportedFormats";
import { message, Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { isOnPremUser } from "../../../../../../helpers/utility/utilities";
import useScanResultsAvailability from "hooks/useScanResultsAvailability";
import useTargetDataset from "views/pages/ScanResultsPage/useTargetDataset";
import { DatasetSource } from "./sources";
import AddDataMenu from "./AddDataMenu";
import ScanResultsModal from "./ScanResultsModal";
import FolderPathsModal from "./FolderPathsModal";
import useExportRows from "./useExportRows";
import scanStyles from "./ScanImport.module.scss";
import useScanExportQueue from "./useScanExportQueue";

interface AddDataContentProps {
  isLoading?: boolean;
  folders: FolderMetadata[];
  onFoldersChange: (folders: FolderMetadata[]) => void;
  onFolderUploadStart: (folderNames: string[]) => void;
  isTableLoading?: boolean;
  batchId?: string;
  /** Scan-result exports still queued or running, so the wizard can hold Continue. */
  onExportsPendingChange?: (count: number) => void;
}

export default function AddDataContent({
  folders,
  isLoading = false,
  onFoldersChange,
  onFolderUploadStart,
  isTableLoading,
  batchId,
  onExportsPendingChange,
}: AddDataContentProps) {
  // Get invalid folders from Redux state
  const invalidFolders = useSelector(getInvalidFolders);
  // Use validation hook to get validation state
  const { isValidating } = useFolderValidation(batchId);

  // Combine valid folders with invalid folders for display
  const allFolders = [...folders, ...invalidFolders];
  const hasFolders = allFolders.length > 0;

  const { startFolderUpload, startFoldersPathsHandler, form } =
    useCreateDataset(batchId);
  const {
    selectedRowKeys,
    disabledRowKeys,
    checkboxDisabledKeys,
    checkboxTooltipData,
    filterState,
    filteredFolders,
    handleSelectionChange,
    handleFilterChange,
    showSelectAll,
    handleSelectAll,
    selectExported,
  } = useFolderSelection(allFolders, onFoldersChange, batchId);

  const { columns, dataSource } = useFolderData(
    filteredFolders,
    filterState,
    allFolders,
  );

  // useCreateDataset returns a fresh startFoldersPathsHandler every render, so listing it
  // would make handlePaths change identity every render too. Read it through a ref
  // instead: what it closes over is not stable - in add-media mode batchId arrives after
  // the first render, and a captured copy would submit folders with no batch.
  const startFoldersPathsRef = useRef(startFoldersPathsHandler);
  startFoldersPathsRef.current = startFoldersPathsHandler;

  const handlePaths = useCallback(
    ({ paths, resetForm }: { paths: string[]; resetForm?: boolean }) => {
      const normalizedPaths = paths.map((p) => p.trim());
      onFolderUploadStart(normalizedPaths);
      return startFoldersPathsRef.current(normalizedPaths, { resetForm });
    },
    [onFolderUploadStart],
  );

  const addExported = useExportedFolders(
    allFolders,
    handlePaths,
    selectExported,
  );

  // On-prem, data is added through dialogs opened from the "Add data" menu; the cloud
  // keeps its inline drop box.
  const showAddDataMenu = isOnPremUser();
  const [openSource, setOpenSource] = useState<DatasetSource | null>(null);
  // Adding media to an existing dataset: scan results it would refuse (another Job, Setup
  // or Recipes) are greyed out in the dialog rather than failing after a long export.
  const isUpdateMode = useLocation().pathname.includes("/update");
  const { newDatasetId } = useParams();
  const { target } = useTargetDataset(isUpdateMode ? newDatasetId : undefined);
  // Paths submitted from the folder dialog, shown as "adding" rows until the folder list
  // has been refetched with them in it (or without them, if the server rejected them).
  const [pendingPaths, setPendingPaths] = useState<string[]>([]);
  const handleFolderPathsSubmit = (values: { paths: string[] }) => {
    const paths = values.paths.map((path) => path.trim());
    setPendingPaths((current) => [...current, ...paths]);
    setOpenSource(null);
    // No reset: the dialog unmounts on close and the form comes back fresh, and
    // resetting an unmounted form only earns an antd warning.
    Promise.resolve(handlePaths({ paths, resetForm: false })).finally(() =>
      setPendingPaths((current) =>
        current.filter((path) => !paths.includes(path)),
      ),
    );
  };
  const { availability: scanResultsAvailability } =
    useScanResultsAvailability();
  const exportQueue = useScanExportQueue(
    useCallback(
      (folderName: string) => addExported([folderName]),
      [addExported],
    ),
  );
  const { pendingCount } = exportQueue;
  useEffect(() => {
    onExportsPendingChange?.(pendingCount);
  }, [onExportsPendingChange, pendingCount]);
  // Unmounting mid-export drops the queue; do not leave the wizard holding Continue.
  useEffect(() => () => onExportsPendingChange?.(0), [onExportsPendingChange]);
  const table = useExportRows({
    items: exportQueue.items,
    pendingPaths,
    folders: allFolders,
    columns,
    dataSource,
    checkboxDisabledKeys,
    checkboxTooltipData,
    onRetry: exportQueue.retry,
    onRemove: exportQueue.remove,
  });
  const showFolderTable = hasFolders || table.dataSource.length > 0;

  const handleFilesChange = (newFiles: File[]) => {
    // Extract folder names from the uploaded files
    const folderNames = Array.from(
      new Set(
        newFiles.map((file) => {
          // Get the first directory name from the file path
          const pathParts = file.webkitRelativePath.split("/");
          return pathParts[0]; // First part is the folder name
        }),
      ),
    );

    // Notify parent about the upload start
    onFolderUploadStart(folderNames);

    startFolderUpload(newFiles);
  };

  const showLoadingState = isLoading || isValidating;

  if (isTableLoading) {
    return <div className={styles.tableLoading}>{<Loader />}</div>;
  }

  return (
    <div
      className={classNames(styles.uploadFolderSection, {
        [styles.withAddDataMenu]: showAddDataMenu,
      })}
    >
      {showAddDataMenu ? (
        <>
          <FolderPathsModal
            isOpen={openSource === "folder"}
            onClose={() => setOpenSource(null)}
            form={form}
            isValidating={isValidating}
            onSubmit={handleFolderPathsSubmit}
          />
          <ScanResultsModal
            isOpen={
              openSource === "scanResults" &&
              !!scanResultsAvailability?.available
            }
            onClose={() => setOpenSource(null)}
            exportStatus={exportQueue.statusByPath}
            target={target}
            targetPending={isUpdateMode && !target}
            onAdd={exportQueue.enqueue}
          />
        </>
      ) : (
        <div className={styles.dropboxWrapper}>
          <FoldersDropBox
            title="Drag and drop folder here"
            textButton="Browse Folders"
            tooltipText={<TooltipText />}
            displayFormats={!hasFolders}
            loading={showLoadingState}
            onFilesChange={handleFilesChange}
          />
        </div>
      )}

      {(showFolderTable || showAddDataMenu) && (
        <div className={styles.fileListSection}>
          <div
            className={classNames(styles.fileListHeaderWrapper, {
              [scanStyles.tableHeader]: showAddDataMenu,
            })}
          >
            <div className={styles.fileListHeader}>
              Select Folders
              <Tooltip
                placement={"right"}
                title={
                  isOnPremUser() ? (
                    <SelectFoldersTooltipText />
                  ) : (
                    <SupportedFormats formatList={folderFormats} />
                  )
                }
              >
                <InfoCircleOutlined className={styles.infoIcon} />
              </Tooltip>
            </div>
            {showAddDataMenu && (
              <AddDataMenu
                onOpen={setOpenSource}
                scanResultsAvailability={scanResultsAvailability}
              />
            )}
          </div>
          {!showFolderTable ? (
            // First visit: point at the menu, so nobody faces an empty step with
            // nowhere obvious to go.
            <div className={scanStyles.emptyState}>
              No data added yet. Use <strong>Add data</strong> to import scan
              results or select a folder.
            </div>
          ) : (
            <div className={styles.fileListTable}>
              <VLTable<FolderTableData>
                columns={table.columns}
                dataSource={table.dataSource}
                selectedRowKeys={selectedRowKeys}
                disabledRowKeys={disabledRowKeys}
                checkboxDisabledKeys={table.checkboxDisabledKeys}
                checkboxTooltipData={table.checkboxTooltipData}
                onTableFilterChange={handleFilterChange}
                onSelectionChange={handleSelectionChange}
                showSelectAll={showSelectAll}
                onSelectAll={handleSelectAll}
                rowKey={"key"}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Add exported scan-result folders as if the user had typed them into the path form and
 * clicked Add, then tick the ones the table will let us tick. Returns `addExported`, which
 * the export queue calls as each folder lands.
 *
 * This lives here because AddDataContent owns the only useCreateDataset instance wired to
 * the form; a second one elsewhere would mint its own antd form and reset that instead.
 *
 * Folders also arrive in router state, from the standalone scan-results page. That is
 * history state - so a browser refresh would replay the POST and earn a "folder already
 * exists" error. Hence both a one-shot ref and clearing the state as soon as it is read.
 */
function useExportedFolders(
  folders: FolderMetadata[],
  handlePaths: (v: { paths: string[]; resetForm?: boolean }) => void,
  selectExported: (folderNames: string[]) => string[],
) {
  const location = useLocation();
  const navigate = useNavigate();
  // Router state is history state: untyped at runtime, and surviving a reload or a tab
  // restored from an older build. Narrow it here, at the boundary, rather than annotating
  // a shape nobody checked - handlePaths trims every entry, and one undefined took the
  // whole page down. Not filtered inside handlePaths: that is also the path form's
  // onFinish, where a silent drop would hide a real bug.
  const raw = location.state?.exportedFolders;
  // Memoised so the effect below is not re-entered on every render by a fresh array.
  const routerFolders = useMemo(
    () =>
      Array.isArray(raw)
        ? raw.filter(
            (name): name is string => typeof name === "string" && !!name.trim(),
          )
        : [],
    [raw],
  );
  const routerSubmitted = useRef(false);
  // Batches waiting for their folders to settle before they can be ticked.
  const pending = useRef<string[][]>([]);
  // Re-run the settle check when a batch is added, not only when folders change.
  const [, setPendingVersion] = useState(0);

  const addExported = useCallback(
    (names: string[]) => {
      if (!names.length) return;
      pending.current = [...pending.current, names];
      setPendingVersion((v) => v + 1);
      handlePaths({ paths: names, resetForm: false });
    },
    [handlePaths],
  );

  useEffect(() => {
    if (routerSubmitted.current || !routerFolders.length) return;
    routerSubmitted.current = true;
    addExported(routerFolders);
    navigate(location.pathname, { replace: true, state: null });
  }, [routerFolders, addExported, location.pathname, navigate]);

  useEffect(() => {
    if (!pending.current.length) return;

    // Selection cannot happen when the POST resolves: set_folders creates folders as
    // `validating`, and the table refuses to tick anything that is not `valid`. Wait for
    // every folder of a batch to settle before deciding - ticking the ones that happen to
    // be ready first would report the stragglers as a Job mismatch they are not.
    //
    // One batch per pass: ticking changes the selection, and a second batch decided in
    // the same pass would read the selection from before the first and overwrite it. The
    // re-render that ticking causes brings the next pass.
    const settledOf = (batch: string[]) =>
      batch.map((name) =>
        folders.find(
          (f) =>
            f.folder_name === name &&
            (f.status === "invalid" || (f.status === "valid" && !!f.metadata)),
        ),
      );
    const index = pending.current.findIndex((batch) =>
      settledOf(batch).every(Boolean),
    );
    if (index === -1) return;
    const batch = pending.current[index];
    const settled = settledOf(batch);
    pending.current = pending.current.filter((_, i) => i !== index);

    const unselected = selectExported(batch);
    if (!unselected.length) return;

    // Say which of the two reasons applies. A folder the pipeline rejected is a
    // different problem from one that simply cannot share a dataset with the rest.
    const invalid = unselected.filter((name) =>
      settled.some((f) => f!.folder_name === name && f!.status === "invalid"),
    );
    const mismatched = unselected.length - invalid.length;
    const reasons = [
      mismatched &&
        `${mismatched} of a different Job or Setup, which a dataset cannot mix`,
      invalid.length && `${invalid.length} rejected as invalid`,
    ].filter(Boolean);

    message.info(
      `${unselected.length} exported folder${unselected.length === 1 ? "" : "s"} ` +
        `added but not selected: ${reasons.join("; ")}.`,
      8,
    );
  });

  return addExported;
}

const SelectFoldersTooltipText = () => (
  <div className={styles.selectFoldersTooltip}>
    <strong>Note</strong>:<br />
    Folder must follow the required structure. In addition, make sure images and
    file contents match, otherwise, the folder will be marked as invalid.
  </div>
);
